import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  Sparkles,
  ArrowRight,
  Loader2,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { aiDiagnoseFacade, aiErrorMessage } from "../lib/api";
import { compressImage } from "../lib/image";
import {
  ReportCard,
  DOMAINS,
  STATUS_CONFIG,
  formatEuro,
} from "./diagnosis/DiagnosisReport";

const HEADER_ANIM = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7 },
};

export default function Diagnosis({ onRequestQuote }) {
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const onPick = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Format non supporté. Choisissez une photo JPG, PNG ou WebP.");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 12 Mo)");
      return;
    }
    try {
      // Compression côté navigateur : upload ~10× plus léger et analyse
      // IA plus rapide, évite les erreurs de taille de requête en production.
      const dataUrl = await compressImage(file, { maxDim: 1400, quality: 0.8 });
      setPhoto(dataUrl);
      setReport(null);
      setError(null);
    } catch {
      toast.error("Impossible de lire cette photo. Essayez une autre image.");
    }
  };

  const runDiagnosis = async () => {
    if (!photo) return;
    setLoading(true);
    setError(null);
    toast.message("Analyse IA en cours…", {
      description: "Diagnostic complet en ~30 secondes",
    });
    try {
      const base64 = photo.split(",")[1] ?? photo;
      const result = await aiDiagnoseFacade(base64);
      setReport(result);
      toast.success("Diagnostic complet généré");
    } catch (e) {
      const msg = aiErrorMessage(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPhoto(null);
    setReport(null);
    setError(null);
  };

  const buildDiagnosisLeadPayload = () => {
    if (!report) return { facade_photo_base64: photo || "" };
    // Sérialise tout le diagnostic IA (score, résumé, 4 domaines, estimations,
    // recommandations) dans custom_options pour que GMC ait tout le rapport
    // dans son email + dans l'admin.
    const domains = report.domains || {};
    const domainLines = Object.entries(domains).flatMap(([k, d]) => {
      const cfg = DOMAINS.find((x) => x.key === k);
      const label = cfg?.label || k;
      const status = STATUS_CONFIG[d.status]?.label || d.status || "—";
      const notes = d.notes ? ` — ${d.notes}` : "";
      const recs = (d.recommendations || []).length
        ? ` · Recos : ${d.recommendations.join(", ")}`
        : "";
      return [`${label} [${status}]${notes}${recs}`];
    });

    const customOptions = [
      `Diagnostic IA GMC — Score global ${report.global_score}/100`,
      report.summary ? `Résumé : ${report.summary}` : null,
      `Estimation travaux : ${formatEuro(report.cost_estimate?.min)} – ${formatEuro(report.cost_estimate?.max)}`,
      `Gain de valeur immobilière estimé : ${formatEuro(report.value_gain_estimate?.min)} – ${formatEuro(report.value_gain_estimate?.max)}`,
      ...domainLines,
    ].filter(Boolean);

    return {
      message:
        "Demande envoyée depuis le Diagnostic IA GMC — rappel rapide souhaité.",
      estimated_min: report.cost_estimate?.min,
      estimated_max: report.cost_estimate?.max,
      custom_options: customOptions,
      facade_photo_base64: photo || "",
    };
  };

  return (
    <section
      id="diagnostic"
      data-testid="diagnosis-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 30%, rgba(255,90,0,0.15) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...HEADER_ANIM} className="mb-12 max-w-3xl">
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <Sparkles size={12} />
            <span>Diagnostic IA · Gratuit</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Diagnostiquez votre façade
            <br />
            <em className="text-[#FF5A00] not-italic font-normal">
              en 30 secondes
            </em>
          </h2>
          <p className="text-neutral-400 text-lg leading-relaxed">
            Notre intelligence artificielle analyse votre maison et génère un rapport
            complet : état de la façade, aménagements extérieurs, estimation des
            travaux et gain de valeur immobilière.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Photo / upload */}
          <div className="lg:col-span-5">
            <div
              data-testid="diagnosis-preview"
              className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0a]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onPick(e.dataTransfer.files?.[0]);
              }}
            >
              {!photo ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="diagnosis-upload-btn"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-neutral-400 hover:text-white hover:bg-white/[0.02] transition-colors"
                >
                  <div className="w-20 h-20 rounded-full border border-white/20 flex items-center justify-center">
                    <Upload size={28} />
                  </div>
                  <div className="text-center px-6">
                    <div className="text-lg text-white font-display mb-1">
                      Uploadez votre maison
                    </div>
                    <div className="text-sm">
                      Photo de l&apos;extérieur (JPG, PNG · max 12 Mo)
                    </div>
                  </div>
                </button>
              ) : (
                <>
                  <img
                    src={photo}
                    alt="Maison à diagnostiquer"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="diagnosis-photo"
                  />
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2 justify-between">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="diagnosis-change-btn"
                      className="pill bg-black/70 backdrop-blur"
                    >
                      <Upload size={14} /> Changer
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      data-testid="diagnosis-reset-btn"
                      className="pill bg-black/70 backdrop-blur"
                    >
                      <RotateCcw size={14} /> Réinitialiser
                    </button>
                  </div>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid="diagnosis-file-input"
              />
            </div>

            {photo && !report && (
              <button
                type="button"
                onClick={runDiagnosis}
                disabled={loading}
                data-testid="diagnosis-run-btn"
                className="btn-primary w-full justify-center mt-4 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Analyse en cours…
                  </>
                ) : error ? (
                  <>
                    <RotateCcw size={16} /> Réessayer l&apos;analyse
                  </>
                ) : (
                  <>
                    Lancer le diagnostic IA <ArrowRight size={16} />
                  </>
                )}
              </button>
            )}

            {/* Message d'erreur explicite (pas de chargement infini) */}
            {error && !loading && (
              <div
                data-testid="diagnosis-error"
                className="mt-4 flex items-start gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-sm text-red-300 leading-relaxed"
              >
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Report */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!report ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-strong rounded-3xl p-8 md:p-10 h-full flex flex-col items-center justify-center text-center min-h-[400px]"
                  data-testid="diagnosis-empty-state"
                >
                  <Sparkles size={36} className="text-[#FF5A00] mb-4" />
                  <h3 className="font-display text-2xl text-white mb-2">
                    Votre rapport apparaîtra ici
                  </h3>
                  <p className="text-sm text-neutral-400 max-w-md">
                    Uploadez une photo de votre maison puis cliquez sur &laquo; Lancer le
                    diagnostic IA &raquo; pour obtenir un rapport complet en 30
                    secondes.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mt-8 max-w-md w-full">
                    {DOMAINS.map((d) => (
                      <div
                        key={d.key}
                        className="flex items-center gap-2 text-xs text-neutral-500"
                      >
                        <d.icon size={14} className="text-[#FF5A00]" />
                        <span>{d.label}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <ReportCard
                  key="report"
                  report={report}
                  buildLeadPayload={buildDiagnosisLeadPayload}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
